import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Generation, api } from '@/lib/api';

export type CatalogPriceEntry = { min_price: number; kind: string };
export type CatalogPricesMap = Record<string, CatalogPriceEntry>;

interface GenerationState {
    selectedModelId: string | null;
    selectedVariantId: string | null;
    generationMode: 'image' | 'video';
    prompt: string;
    aspectRatio: string;
    resolution: string;
    outputFormat: string;
    modelParams: Record<string, unknown>;
    referenceImages: string[]; // List of uploaded images
    selectedStyle: string;

    // Catalog Prices (from server)
    catalogPrices: CatalogPricesMap;
    _catalogPricesFetched: boolean;
    fetchCatalogPrices: () => Promise<void>;

    isGenerating: boolean;
    currentTaskId: string | null;
    history: { id: string; slug: string }[];
    pendingGenerations: Generation[];
    _hasHydrated: boolean;
    
    // View State Persistence
    currentView: 'landing' | 'studio' | 'tool' | 'apps' | 'gallery' | 'templates' | 'effects';
    activeToolId: string | null;
    activeAppId: string | null;

    // Preset deep-link: full preset data from shared link
    pendingPresetRef: string | null;
    setPendingPresetRef: (url: string | null) => void;
    pendingPreset: { id: string; media_url?: string; prompt?: string; defaults?: Record<string, unknown>; metadata?: Record<string, unknown> } | null;
    setPendingPreset: (preset: { id: string; media_url?: string; prompt?: string; defaults?: Record<string, unknown>; metadata?: Record<string, unknown> } | null) => void;

    setSelectedModelId: (id: string | null) => void;
    setSelectedVariantId: (id: string | null) => void;
    setGenerationMode: (mode: 'image' | 'video') => void;
    setPrompt: (text: string) => void;
    setSelectedStyle: (style: string) => void;
    setAspectRatio: (ratio: string) => void;
    setResolution: (res: string) => void;
    setOutputFormat: (fmt: string) => void;
    setModelParams: (params: Record<string, unknown>) => void;
    updateModelParam: (key: string, value: unknown) => void;
    setReferenceImage: (url: string | null) => void;
    setReferenceImages: (urls: string[]) => void;
    addReferenceImage: (url: string) => void;
    removeReferenceImage: (url: string, index?: number) => void;
    setIsGenerating: (isGenerating: boolean) => void;
    setCurrentTaskId: (id: string | null) => void;
    setHistory: (history: { id: string; slug: string }[]) => void;
    addPendingGeneration: (gen: Generation) => void;
    updatePendingGeneration: (id: string, updates: Partial<Generation>) => void;
    removePendingGeneration: (id: string) => void;
    setHasHydrated: (hasHydrated: boolean) => void;
    
    setCurrentView: (view: 'landing' | 'studio' | 'tool' | 'apps' | 'gallery' | 'templates' | 'effects') => void;
    setActiveToolId: (id: string | null) => void;
    setActiveAppId: (id: string | null) => void;
    
    resetGeneratorState: () => void;
}

export const useGenerationStore = create<GenerationState>()(
    persist(
        (set) => ({
            selectedModelId: null,
            selectedVariantId: null,
            generationMode: 'image',
            prompt: '',
            aspectRatio: '1:1',
            resolution: '1024x1024',
            outputFormat: 'png',
            modelParams: {},
            referenceImages: [],
            selectedStyle: 'none',

            catalogPrices: {},
            _catalogPricesFetched: false,
            fetchCatalogPrices: async () => {
                if (useGenerationStore.getState()._catalogPricesFetched) return;
                try {
                    const response = await api.get('/pricing/catalog-prices');
                    const data: CatalogPricesMap = response.data ?? {};
                    set({ catalogPrices: data, _catalogPricesFetched: true });
                } catch (error) {
                    // Catalog prices fetch failed — using defaults
                }
            },
            isGenerating: false,
            currentTaskId: null,
            history: [],
            pendingGenerations: [],
            _hasHydrated: false,
            
            currentView: 'landing',
            activeToolId: null,
            activeAppId: null,
            pendingPresetRef: null,
            setPendingPresetRef: (url) => set({ pendingPresetRef: url }),
            pendingPreset: null,
            setPendingPreset: (preset) => set({ pendingPreset: preset, pendingPresetRef: preset?.media_url ?? null }),

            resetGeneratorState: () => set({
                selectedModelId: null,
                selectedVariantId: null,
                prompt: '',
                aspectRatio: '1:1',
                resolution: '1024x1024',
                outputFormat: 'png',
                modelParams: {},
                referenceImages: [],
                selectedStyle: 'none',
                // Do not reset history or pendingGenerations
            }),

            setSelectedModelId: (id) => set({ selectedModelId: id, selectedVariantId: null, modelParams: {} }),
            setSelectedVariantId: (id) => set({ selectedVariantId: id }),
            setGenerationMode: (mode) => set({ generationMode: mode }),
            setPrompt: (text) => set({ prompt: text }),
            setSelectedStyle: (style) => set({ selectedStyle: style }),
            setAspectRatio: (ratio) => set({ aspectRatio: ratio }),
            setResolution: (res) => set({ resolution: res }),
            setOutputFormat: (fmt) => set({ outputFormat: fmt }),
            setModelParams: (params) => set({ modelParams: params }),
            updateModelParam: (key, value) => set((state) => ({ 
                modelParams: { ...state.modelParams, [key]: value } 
            })),
            setReferenceImage: (url) => set({ referenceImages: url ? [url] : [] }),
            setReferenceImages: (urls: string[]) => set({ referenceImages: urls }),
            addReferenceImage: (url: string) => set((state) => ({ 
                referenceImages: [...state.referenceImages, url] 
            })),
            removeReferenceImage: (url: string, index?: number) => set((state) => {
                if (index !== undefined) {
                    return { referenceImages: state.referenceImages.filter((_, i) => i !== index) };
                }
                const idx = state.referenceImages.indexOf(url);
                if (idx === -1) return state;
                return { referenceImages: state.referenceImages.filter((_, i) => i !== idx) };
            }),
            setIsGenerating: (loading) => set({ isGenerating: loading }),
            setCurrentTaskId: (id) => set({ currentTaskId: id }),
            setHistory: (history) => set({ history }),
            addPendingGeneration: (gen) => {
                try { localStorage.setItem('app:hasHistory', '1'); } catch {}
                set((state) => {
                    // Auto-cleanup: remove completed/failed items older than 30 min
                    const cutoff = Date.now() - 30 * 60 * 1000;
                    const cleaned = state.pendingGenerations.filter(g => {
                        if (g.status === 'COMPLETED' || g.status === 'FAILED') {
                            return (g.created_at ? new Date(g.created_at).getTime() : Date.now()) > cutoff;
                        }
                        return true;
                    });
                    return { pendingGenerations: [gen, ...cleaned] };
                });
            },
            updatePendingGeneration: (id, updates) => set((state) => ({
                pendingGenerations: state.pendingGenerations.map(g => 
                    g.id === id ? { ...g, ...updates } : g
                )
            })),
            removePendingGeneration: (id) => set((state) => ({
                pendingGenerations: state.pendingGenerations.filter(g => g.id !== id)
            })),
            setHasHydrated: (hasHydrated) => set({ _hasHydrated: hasHydrated }),
            
            setCurrentView: (view) => set({ currentView: view }),
            setActiveToolId: (id) => set({ activeToolId: id }),
            setActiveAppId: (id) => set({ activeAppId: id }),
        }),
        {
            name: 'generation-storage', // unique name
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                selectedModelId: state.selectedModelId,
                selectedVariantId: state.selectedVariantId,
                prompt: state.prompt,
                aspectRatio: state.aspectRatio,
                resolution: state.resolution,
                outputFormat: state.outputFormat,
                modelParams: state.modelParams,
                referenceImages: state.referenceImages,
                selectedStyle: state.selectedStyle,
                currentTaskId: state.currentTaskId,
                history: state.history,
                // Persist only PENDING items so the safety-net poller (in
                // AuthProvider) can pick up jobs left behind by a refresh.
                // Completed/failed items are visible in /gallery already.
                pendingGenerations: state.pendingGenerations.filter(p => p.status === 'PENDING'),
                currentView: state.currentView,
                activeToolId: state.activeToolId,
                activeAppId: state.activeAppId,
                generationMode: state.generationMode,
                // We do NOT persist isGenerating to avoid stuck spinners on reload
                // But currentTaskId will allow us to resume checking status
            }),
            skipHydration: true,
            onRehydrateStorage: () => (state) => {
                state?.setHasHydrated(true);
            },
        }
    )
);
